import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from './AuthContext';
import { dataFlowLog, summarizeCollection, summarizeCommunity } from '../utils/dataFlowLogger';
import useQueryScope from '../queries/useQueryScope';
import { readOfflineSnapshot, writeOfflineSnapshot, ownsQueryScope, communityIdOf } from '../queries/queryOwnership';
import queryKeys from '../queries/queryKeys';

const defaultCommunityContext = {
  community: null,
  setCommunity: () => {},
  stats: null,
  setStats: () => {},
  lsa: [],
  setLsa: () => {},
};

const CommunityContext = createContext(defaultCommunityContext);

export const useCommunityContext = () => useContext(CommunityContext);

const resolveCommunityId = (communityValue, fallbackCommunityId = null) => {
  if (Array.isArray(communityValue)) {
    const resolvedFromArray = communityValue
      .map((item) => resolveCommunityId(item))
      .find(Boolean);
    if (resolvedFromArray) return resolvedFromArray;
  }

  const candidateValues = [
    communityValue?._id,
    communityValue?.id,
    communityValue?.communityId,
    fallbackCommunityId,
  ];

  const resolved = candidateValues.find((candidate) => {
    if (typeof candidate === 'string') return candidate.trim().length > 0;
    if (typeof candidate === 'number') return Number.isFinite(candidate);
    return false;
  });

  if (typeof resolved === 'number') return String(resolved);
  return typeof resolved === 'string' ? resolved.trim() : null;
};

const normalizeCommunityValue = (communityValue, fallbackCommunityId = null) => {
  if (Array.isArray(communityValue)) {
    const normalizedFromArray = communityValue
      .map((item) => normalizeCommunityValue(item, fallbackCommunityId))
      .find(Boolean);
    return normalizedFromArray || normalizeCommunityValue(fallbackCommunityId);
  }

  if (communityValue && typeof communityValue === 'object' && !Array.isArray(communityValue)) {
    const communityId = resolveCommunityId(communityValue, fallbackCommunityId);
    return communityId
      ? { ...communityValue, _id: communityId }
      : communityValue;
  }

  if (typeof communityValue === 'string' && communityValue.trim()) {
    return { _id: communityValue.trim() };
  }

  if (typeof communityValue === 'number' && Number.isFinite(communityValue)) {
    return { _id: String(communityValue) };
  }

  if (typeof fallbackCommunityId === 'string' && fallbackCommunityId.trim()) {
    return { _id: fallbackCommunityId.trim() };
  }

  return null;
};

const normalizeCommunityFromUser = (user) => {
  if (!user || typeof user !== 'object') return null;
  const fallbackCommunityId = resolveCommunityId(user?.community, user?.communityId);
  return normalizeCommunityValue(user?.community ?? user?.communityId, fallbackCommunityId);
};

const isMissingCommunityValue = (value, fallbackValue) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0 && Array.isArray(fallbackValue) && fallbackValue.length > 0;
  return false;
};

const mergeCommunityMetadata = (currentCommunity, authCommunity) => {
  const normalizedAuthCommunity = normalizeCommunityValue(authCommunity);
  if (!normalizedAuthCommunity) return normalizeCommunityValue(currentCommunity);

  const normalizedCurrentCommunity = normalizeCommunityValue(
    currentCommunity,
    resolveCommunityId(normalizedAuthCommunity),
  );
  if (!normalizedCurrentCommunity) return normalizedAuthCommunity;

  const currentCommunityId = resolveCommunityId(normalizedCurrentCommunity);
  const authCommunityId = resolveCommunityId(normalizedAuthCommunity);
  if (currentCommunityId && authCommunityId && currentCommunityId !== authCommunityId) {
    return normalizedAuthCommunity;
  }

  const merged = { ...normalizedAuthCommunity };
  Object.entries(normalizedCurrentCommunity).forEach(([key, value]) => {
    if (isMissingCommunityValue(value, merged[key])) return;
    merged[key] = value;
  });

  if (!merged._id && (currentCommunityId || authCommunityId)) {
    merged._id = currentCommunityId || authCommunityId;
  }

  return merged;
};

export const CommunityProvider = ({ children }) => {
  const { user } = useAuthContext() || {};
  const { scope, isCurrent } = useQueryScope({ communityId: communityIdOf(user?.community ?? user?.communityId) });
  const queryClient = useQueryClient();
  const authCommunity = useMemo(() => normalizeCommunityFromUser(user), [user]);

  const communityQueryKey = useMemo(() => queryKeys.community.meta(scope), [scope]);
  const statsQueryKey = useMemo(() => queryKeys.community.stats(scope), [scope]);
  const lsaQueryKey = useMemo(() => queryKeys.community.lsa(scope), [scope]);
  const initialCommunity = useMemo(
    () => mergeCommunityMetadata(readOfflineSnapshot('communityMeta', scope, null), authCommunity),
    [authCommunity, scope],
  );
  const initialStats = useMemo(() => readOfflineSnapshot('communityStats', scope, null), [scope]);
  const initialLsa = useMemo(() => readOfflineSnapshot('lsa', scope, []), [scope]);

  const communityQuery = useQuery({
    queryKey: communityQueryKey,
    queryFn: async () => null,
    enabled: false,
    initialData: initialCommunity,
  });
  const statsQuery = useQuery({
    queryKey: statsQueryKey,
    queryFn: async () => null,
    enabled: false,
    initialData: initialStats,
  });
  const lsaQuery = useQuery({
    queryKey: lsaQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: initialLsa,
  });

  const community = communityQuery.data ?? null;
  const stats = statsQuery.data ?? null;
  const lsa = useMemo(
    () => (Array.isArray(lsaQuery.data) ? lsaQuery.data : []),
    [lsaQuery.data],
  );

  // A pre-login bootstrap setter can target only its explicitly captured,
  // still-active owner. A user ID alone is not proof of session ownership.
  const canWrite = useCallback((target, explicit, userId) => (!userId || String(userId) === target?.actorId)
    && (explicit ? ownsQueryScope(target) : isCurrent()), [isCurrent]);
  const setCommunity = useCallback((nextCommunity, options = {}) => {
    const target = options.scope || scope;
    if (!canWrite(target, Boolean(options.scope), options.userId)) return;
    const targetKey = queryKeys.community.meta(target);
    const current = queryClient.getQueryData(targetKey) ?? null;
    const resolved = typeof nextCommunity === 'function' ? nextCommunity(current) : nextCommunity;
    queryClient.setQueryData(targetKey, resolved ?? null);
    writeOfflineSnapshot('communityMeta', target, resolved ?? null);
  }, [queryClient, scope, canWrite]);
  const setStats = useCallback((nextStats, options = {}) => {
    const target = options.scope || scope;
    if (!canWrite(target, Boolean(options.scope), options.userId)) return;
    const targetKey = queryKeys.community.stats(target);
    const current = queryClient.getQueryData(targetKey) ?? null;
    const resolved = typeof nextStats === 'function' ? nextStats(current) : nextStats;
    queryClient.setQueryData(targetKey, resolved ?? null);
    writeOfflineSnapshot('communityStats', target, resolved ?? null);
  }, [queryClient, scope, canWrite]);
  const setLsa = useCallback((nextLsa, options = {}) => {
    const target = options.scope || scope;
    if (!canWrite(target, Boolean(options.scope), options.userId)) return;
    const targetKey = queryKeys.community.lsa(target);
    const current = queryClient.getQueryData(targetKey) || [];
    const resolved = typeof nextLsa === 'function' ? nextLsa(current) : nextLsa;
    const normalized = Array.isArray(resolved) ? resolved : [];
    queryClient.setQueryData(targetKey, normalized);
    writeOfflineSnapshot('lsa', target, normalized);
  }, [queryClient, scope, canWrite]);

  useEffect(() => {
    if (!authCommunity) return;
    setCommunity((current) => mergeCommunityMetadata(current, authCommunity));
  }, [authCommunity, setCommunity]);

  useEffect(() => {
    dataFlowLog('community-context.updated', {
      community: summarizeCommunity(community),
      lsa: summarizeCollection(lsa),
      stats: stats
        ? {
          communityMembersCount: stats?.communityMembersCount ?? null,
          activitiesCount: stats?.activitiesCount ?? null,
          eventsCount: stats?.eventsCount ?? null,
        }
        : null,
    });
  }, [community, lsa, stats]);

  const value = useMemo(
    () => ({ community, setCommunity, stats, setStats, lsa, setLsa }),
    [community, lsa, setCommunity, setLsa, setStats, stats],
  );

  return (
    <CommunityContext.Provider value={value}>
      {children}
    </CommunityContext.Provider>
  );
};
